// ui/EndOfHandModal.jsx: Pillar 4 end-of-hand scoring modal (M2.c-7).
//
// Renders as a DOM overlay (native <dialog> with showModal), not on the
// canvas. Why: focus trap, escape key, and screen-reader landmark all
// come free from the platform; canvas-drawn modals would need to
// rebuild that infrastructure.
//
// Timeline:
//   t=0, dialog opens, recap row starts revealing tricks
//   t=120ms, trick 1 visible
//   t=240ms, trick 2 visible
//   …
//   t=600ms, all 5 tricks visible, audio.emit fires once.
//   Scores and Continue are ready at t=0.
//
// With reduced motion (the OS setting or the in-app switch): t=0 shows
// all 5 tricks. The audio event still fires once on the same frame.
//
// Pure logic for label/audio/timing lives in ./end-of-hand.js; this
// file is JSX + the rAF-driven open lifecycle.

import { useState, useEffect, useRef } from 'preact/hooks';
import { useTrickAnimation, usePrefersReducedMotion } from './useTrickAnimation.js';
import { useSettings } from './useSettings.js';
import { reducedMotionActive } from '../settings.js';
import * as audio from '../audio/events.js';
import { ReadyCountdown } from './ReadyCountdown.jsx';
import { headlineParts } from './headline-parts.js';
import {
  headlineFor,
  formatPartnerHand,
  audioEventForResult,
  outcomeVisuals,
  computeRecapPlan,
  recapTickIndex,
  trickBoxAccessibleName,
  fitTrickWinnerLabels,
} from './end-of-hand.js';
import { tricksWonByTeam, recapSentence, historyRows } from './recap-text.js';
import { ScoreBlock, MiniCard } from './CreamTable.jsx';
import { shortName } from '../canvas/cream-layout.js';
import { SUIT_WORD } from '../views/cream-table-model.js';
// The sheets' sheets, in the order the game view loads them (later wins an
// equal-specificity tie): this file can be reached before the view's own imports.
import '../styles/room.css';
import '../styles/networked-game.css';
import '../styles/fonts.css';
import '../styles/cream-table.css';
import '../styles/cream-game-panels.css';
import { getParticleLayer } from './particle-layer-singleton.js';
import { applyScreenShake } from './screen-shake.js';

/**
 * Show the final score on the first frame. Renders "US: 2 → 4".
 *
 * @param {{label:'US'|'THEM', prev:any, next:any}} props
 */
function RollingDelta({ label, prev, next }) {
  const p = (typeof prev === 'number' && Number.isFinite(prev)) ? prev : 0;
  const n = (typeof next === 'number' && Number.isFinite(next)) ? next : 0;
  return <>{`${label}: ${p} → ${n}`}</>;
}

function teamOfSeat(seat) { return (seat === 1 || seat === 3) ? 'team_a' : 'team_b'; }
/**
 * Particle origin = center of the dialog (so chip bursts radiate from
 * the score-label region). Falls back to viewport center if the dialog
 * isn't laid out yet (defensive, should rarely fire).
 */
function modalCenterOrigin(dialog) {
  if (dialog && typeof dialog.getBoundingClientRect === 'function') {
    const r = dialog.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) {
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
  }
  if (typeof window !== 'undefined') {
    return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  }
  return { x: 0, y: 0 };
}

// B3b item 1: the recap box text is measured in the font the box really
// uses (Tiny5 at 16 px, or the system font under the system-font switch).
/** @type {CanvasRenderingContext2D|null|undefined} */
let recapMeasureCtx;

/** Rendered width of a recap label, in px, in the given CSS font. */
function measureRecapText(text, font) {
  if (recapMeasureCtx === undefined) {
    recapMeasureCtx = document.createElement('canvas').getContext('2d');
    if (!recapMeasureCtx) {
      console.warn('[EndOfHandModal] no canvas 2D context; recap names assume 1em per letter');
    }
  }
  if (!recapMeasureCtx) return Array.from(text).length * 16;
  recapMeasureCtx.font = font;
  return recapMeasureCtx.measureText(text).width;
}

/** The text room inside one recap box (its content box), or null before layout. */
function recapLabelRoom(row) {
  const box = row?.querySelector('[data-test-trick]');
  if (!box || box.clientWidth === 0) return null;
  const s = getComputedStyle(box);
  // 1px spare covers rounding between canvas and DOM text widths.
  return box.clientWidth - parseFloat(s.paddingLeft) - parseFloat(s.paddingRight) - 1;
}

/** The CSS font the first recap box draws its text in. */
function recapBoxFont(row) {
  const box = row?.querySelector('[data-test-trick]');
  if (!box) return '16px monospace';
  const s = getComputedStyle(box);
  return `${s.fontWeight} ${s.fontSize} ${s.fontFamily}`;
}

/**
 * @param {{
 *   open: boolean,
 *   summary: object,
 *   tricks?: Array<{ winner_seat: number }>|null,
 *   partnerHand?: string[]|null,
 *   trumpSuit?: string|null,
 *   showPartnerReveal?: boolean,
 *   myTeam?: 'team_a'|'team_b',
 *   seatLabels?: Record<number, string>|null,
 *   handNumber?: number|null,
 *   target?: number,
 *   makerSeat?: number|null,
 *   tableMode?: string,
 *   onContinue: () => void,
 *   waiting?: boolean,
 *   waitingText?: string|null,
 *   deadlineMs?: number|null,
 *   clockOffsetMs?: number,
 * }} props
 */
export function EndOfHandModal({
  open,
  summary,
  tricks,
  partnerHand,
  trumpSuit,
  showPartnerReveal,
  myTeam = 'team_a',
  seatLabels = null,
  handNumber = null,
  target = 10,
  makerSeat = null,
  tableMode = 'desk',
  onContinue,
  waiting = false,
  waitingText = null,
  deadlineMs = null,
  clockOffsetMs = 0,
}) {
  /** @type {{current: HTMLDialogElement|null}} */
  const dialogRef = useRef(null);
  const continueRef = useRef(/** @type {HTMLButtonElement|null} */ (null));
  // The OS setting or the in-app Reduce motion switch: either shows the
  // whole recap at once, with no particles and no shake.
  const [settings] = useSettings();
  const reducedMotion = reducedMotionActive(settings.reduced_motion_override, usePrefersReducedMotion());
  const plan = computeRecapPlan(reducedMotion);
  const [recapDone, setRecapDone] = useState(false);
  const audioFiredRef = useRef(false);
  const recapRowRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const [labelRoom, setLabelRoom] = useState(/** @type {number|null} */ (null));
  const [labelFont, setLabelFont] = useState('16px monospace');

  const elapsedMs = useTrickAnimation({
    active: open && !plan.instant,
    durationMs: plan.total_ms || 1,  // 0-duration trips a 0 >= 0 race; use 1ms.
    onComplete: () => setRecapDone(true),
  });
  const tickIndex = plan.instant
    ? plan.tick_count
    : recapTickIndex(elapsedMs, plan);

  // Open/close lifecycle. Native showModal sets up focus trap + escape
  // key. Reset auxiliary state on each open so reopening starts clean.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open) {
      setRecapDone(plan.instant);
      audioFiredRef.current = false;
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    } else {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [open, plan.instant]);

  // B3b item 1: measure the room inside a recap box once the dialog is
  // shown (this runs after showModal above) and again when it resizes,
  // when the pixel fonts arrive and when the system-font switch flips.
  const hasSummary = !!summary;
  useEffect(() => {
    const row = recapRowRef.current;
    if (!open || !row) return undefined;
    const measure = () => { setLabelRoom(recapLabelRoom(row)); setLabelFont(recapBoxFont(row)); };
    measure();
    const cleanups = [];
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(measure);
      observer.observe(row);
      cleanups.push(() => observer.disconnect());
    } else {
      window.addEventListener('resize', measure);
      cleanups.push(() => window.removeEventListener('resize', measure));
    }
    const bodyWatch = new MutationObserver(measure);
    bodyWatch.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    cleanups.push(() => bodyWatch.disconnect());
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    return () => cleanups.forEach((fn) => fn());
  }, [open, hasSummary]);

  // Finish the trick highlight and play its outcome sound.
  useEffect(() => {
    if (!recapDone) return;
    if (!audioFiredRef.current) {
      // M5 #9, pass scoring_team so euchred routes to euchred_us vs
      // euchred_them. myTeam defaults to 'team_a' (single-player POV);
      // networked play threads its seat-derived team through props.
      const ev = audioEventForResult(
        summary?.result, summary?.was_loner, summary?.scoring_team, myTeam);
      if (ev) audio.emit(ev, { delta: summary?.delta, scoring_team: summary?.scoring_team });
      audioFiredRef.current = true;

      // M5 #12 + #15, chip particle burst (or fire burst for loner
      // sweeps) + shake at the score-label moment. outcomeVisuals
      // returns 0 particles + 'none' shake under reduced-motion.
      // particleType picks BURST vs FIRE.
      const v = outcomeVisuals(summary, myTeam, reducedMotion);
      if (v.particleCount > 0) {
        const center = modalCenterOrigin(dialogRef.current);
        getParticleLayer().burst(v.particleType, center, {
          count: v.particleCount,
          color: v.particleColor,
        });
      }
      if (v.shakeTier !== 'none') {
        applyScreenShake(v.shakeTier);
      }
    }
  }, [recapDone, summary, myTeam, reducedMotion]);

  // Continue is ready from the first dialog frame.
  useEffect(() => {
    if (open) continueRef.current?.focus();
  }, [open]);

  if (!summary) return null;

  const headline = headlineFor(summary, myTeam);
  const safeTricks = Array.isArray(tricks) ? tricks.slice(0, 5) : [];
  const partner = showPartnerReveal ? formatPartnerHand(partnerHand, trumpSuit) : null;
  // T12: each box names the trick winner, 'You', 'Ann', 'Bot 3', not
  // an S{n} seat code nobody uses anywhere else.
  const winnerNames = Array.from({ length: 5 }, (_, i) => {
    const seat = safeTricks[i]?.winner_seat;
    return seat ? (seatLabels?.[seat] || `Bot ${seat}`) : '';
  });
  const winnerLabels = fitTrickWinnerLabels(winnerNames, labelRoom ?? Infinity, (text) => measureRecapText(text, labelFont));
  const otherTeam = myTeam === 'team_a' ? 'team_b' : 'team_a';
  const won = tricksWonByTeam(safeTricks, myTeam);
  const sentence = recapSentence({
    summary,
    myTeam,
    makerTeam: makerSeat ? teamOfSeat(makerSeat) : null,
    makerName: makerSeat ? (seatLabels?.[makerSeat] || `Bot ${makerSeat}`) : '',
    trumpWord: trumpSuit ? (SUIT_WORD[trumpSuit] ?? '').toLowerCase() : '',
    won,
  });
  const rows = historyRows(safeTricks, myTeam);
  const nameOf = (seat) => shortName(seatLabels?.[seat] || `Bot ${seat}`).toUpperCase();
  const { lead, points } = headlineParts(headline);
  // The pixel face has no em dash: "We got euchred — THEM +2" reads "We got euchred: THEM +2".
  const shownLead = lead.replace(/!\s—\s$/, '! ').replace(/\s—\s$/, ': ');

  return (
    <dialog
      ref={dialogRef}
      class="cream-sheet recap-sheet"
      data-modal="end-of-hand"
      role="dialog"
      aria-modal="true"
      aria-labelledby="eoh-title"
      onCancel={(e) => e.preventDefault()}
    >
      <div class="cs-body">
        <p class="cs-eyebrow">{handNumber != null ? `HAND ${handNumber} COMPLETE` : 'HAND COMPLETE'}</p>
        <h2 id="eoh-title" class="cs-title" data-test-result={summary.result}>
          {/* B4b-1 U5: "THEM +2" never breaks, so the points stay with
              their word when the headline wraps (375x667). */}
          {shownLead}
          {points && <span class="nowrap" data-testid="eoh-headline-points">{points}</span>}
        </h2>

        {/* Per-trick recap row, fills in over 0.6s (or instant when
            reduced-motion). B3b D3: the five boxes name the trick winner;
            the caption and legend say what the colours mean. */}
        <p class="cs-caption" data-test-recap-caption>Trick winners</p>
        <p class="cs-legend" data-test-recap-legend>Gold is us. Teal is them.</p>
        <div class="cs-strip" ref={recapRowRef} data-test-recap aria-label="Trick recap">
          {Array.from({ length: 5 }).map((_, i) => {
            const revealed = i < tickIndex;
            const winnerSeat = safeTricks[i]?.winner_seat;
            const team = winnerSeat ? teamOfSeat(winnerSeat) : null;
            const winClass = revealed && team ? (team === myTeam ? ' win-us' : ' win-them') : '';
            const winnerName = winnerNames[i];
            return (
              <div
                key={i}
                class={`cs-box${winClass}`}
                role="img"
                aria-label={trickBoxAccessibleName(i, revealed ? winnerSeat : null, winnerName, myTeam)}
                data-test-trick={i + 1}
                data-test-revealed={revealed ? '1' : '0'}
                title={revealed && winnerName ? `Trick ${i + 1}: ${winnerName}` : undefined}
              >
                {revealed && winnerSeat
                  ? <span data-test-winner-name>{winnerLabels[i]}</span>
                  : i + 1}
              </div>
            );
          })}
        </div>

        {/* Score blocks show final totals as soon as the dialog opens;
            under each, the totals before and after the hand. */}
        {summary.prev_scores && summary.new_scores && (
          <div class="cs-scores" data-test-score-block>
            <div class="cs-team team-US">
              <ScoreBlock sheet label="US" score={summary.new_scores?.[myTeam] ?? 0} target={target} tricks={won.US} mode={tableMode} />
              <div class="cs-delta" data-test-us>
                <RollingDelta label="US" prev={summary.prev_scores?.[myTeam]} next={summary.new_scores?.[myTeam]} />
              </div>
            </div>
            <div class="cs-team team-THEM">
              <ScoreBlock sheet label="THEM" score={summary.new_scores?.[otherTeam] ?? 0} target={target} tricks={won.THEM} mode={tableMode} />
              <div class="cs-delta" data-test-them>
                <RollingDelta label="THEM" prev={summary.prev_scores?.[otherTeam]} next={summary.new_scores?.[otherTeam]} />
              </div>
            </div>
          </div>
        )}

        <p class="cs-sentence" data-testid="eoh-sentence">{sentence}</p>

        {/* "What partner held" only when the setting is on. */}
        {partner && (
          <div class="cs-partner" data-test-partner-reveal aria-label={partner.ariaText}>
            <div class="cs-caption">Partner held</div>
            <div class="cs-partner-cards">
              {partner.cards.map((c, i) => (
                <span key={i} class="cs-chip" aria-label={c.label}>{c.display}</span>
              ))}
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <details class="cs-history" data-testid="eoh-history">
            <summary>TRICK HISTORY</summary>
            {rows.map((r) => (
              <div class="history-row" key={r.n} data-testid="eoh-history-row">
                <span class="hr-num">{r.n}</span>
                {/* The words sit beside the cards, or under them when the row is too narrow. */}
                <div class="hr-main">
                  <div class="hr-cards">
                    {r.plays.map((p) => (
                      <span class="hr-card" key={p.card_id}>
                        <MiniCard cardId={p.card_id} winner={p.seat === r.winner} team={r.winTeam} />
                      </span>
                    ))}
                  </div>
                  <span class="hr-words">
                    <span>{nameOf(r.leader)} LED</span>
                    <span class={`hr-win win-${r.winTeam}`}>{nameOf(r.winner)} WON</span>
                  </span>
                </div>
              </div>
            ))}
          </details>
        )}

        {/* After the click, the button waits on the table. */}
        <div class="cs-actions">
          {deadlineMs != null && (
            <ReadyCountdown deadlineMs={deadlineMs} clockOffsetMs={clockOffsetMs} />
          )}
          <button
            ref={continueRef}
            type="button"
            class="btn btn-primary"
            data-action="continue"
            disabled={waiting}
            aria-disabled={waiting}
            onClick={() => {
              if (waiting) return;
              onContinue();
            }}
          >
            {waiting ? (waitingText || 'Waiting for the others…') : 'Continue'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
